// A sculpted bench (the Sculpted bench tool, docs/claude/lattice.md): a solid
// profile swept along its run (sculptRun.ts), bent by its ordered stack of
// cages (lattice.ts). Only figures are stored on the room
// (`ProductInstance.sculptedBenches`); the starting mesh — the profile laid at
// stations along the run, its long edges divided — is built in the bench's own
// frame (along, out, up), every point bent by the stack there and then laid on
// the run. Every view reads the one resolved layout: the plan (its outline and
// seat lines), sections (its faces), 3D (its mesh), the hit test and the cage
// handles. Pure.

import type {
  BenchProfilePoint,
  Lattice,
  ProductInstance,
  SculptedBench,
  SculptEnd,
  Vec2,
} from '../shared/types';
import {
  BENCH_TIER_COLORS,
  STEAM_BENCH_TIER_COLORS,
} from './benches';
import {
  profileExtent,
  type ProfilePt,
  profileRing,
  sliceAt,
  type SweepFace,
  type Vec3,
} from './benchProfile';
import { pointInPolygon } from './geometry';
import {
  activeCages,
  cageFolds,
  cageLines,
  type CagePick,
  cagePoint,
  deformStack,
  type F3,
  type FrameExtent,
  pointFreedom,
} from './lattice';
import {
  contentMemo,
  HEAVY_FIELDS,
} from './plainData';
import {
  paramOf,
  runChain,
  type RunFace,
  type SculptRun,
  sculptRun,
  stationOf,
} from './sculptRun';
import { earClip } from '../shared/triangulate';

/** The longest a stretch of the starting mesh runs along the bench, and the
 *  longest a profile edge is left undivided (mm). */
export const SCULPT_STEP = 50;
/** The fewest stations a corner of the run is laid with. */
const CORNER_STATIONS = 12;

export function sculptedBenchesOf(inst: ProductInstance): SculptedBench[] {
  return inst.sculptedBenches ?? [];
}

/** A sculpted bench's colour: its own, else the room's bench colour, else a
 *  high tier's. */
export function sculptColor(inst: ProductInstance, sb: SculptedBench): string {
  return sb.color ?? inst.benchColor ?? (inst.category === 'steamroom' ? STEAM_BENCH_TIER_COLORS : BENCH_TIER_COLORS).high;
}

/** What may be wrong with a sculpted bench: a partition stands across its run
 *  (`blocked`: it stops there), a cage turns the solid inside out (`fold`), a
 *  cage reaches past the run's ends (`clipped`, after the run got shorter). */
export type SculptWarning = 'blocked' | 'clipped' | 'fold';

/** The profile ring with its long edges divided, each added point a sample. */
export function sculptRing(profile: readonly BenchProfilePoint[]): ProfilePt[] {
  const ring = profileRing(profile);
  const out: ProfilePt[] = [];
  for (let k = 0; k < ring.length; k++) {
    const a = ring[k], b = ring[(k + 1) % ring.length];
    out.push(a);
    const n = Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / SCULPT_STEP);
    for (let i = 1; i < n; i++) out.push({ x: a.x + ((b.x - a.x) * i) / n, y: a.y + ((b.y - a.y) * i) / n, s: true });
  }
  return out;
}

/** The solid's own extent in its frame: the run's length, the profile's depth and height. */
export function sculptExtent(run: Pick<SculptRun, 'length'>, profile: readonly BenchProfilePoint[]): FrameExtent {
  const { width, height } = profileExtent(profile);
  return { along: [0, run.length], out: [0, width], up: [0, height] };
}

/** A sculpted bench resolved: its run, its frame extent, the mesh (room-local
 *  plan x, y and height z at each station of each ring point), the faces a
 *  section cuts and sees, its plan outline and seat lines, its warnings. */
export interface SculptLayout {
  run: SculptRun;
  extent: FrameExtent;
  ring: ProfilePt[];
  stations: number[];
  pts: Vec3[][];
  faces: SweepFace[];
  outline: Vec2[];
  seatLines: Vec2[][];
  warnings: SculptWarning[];
  /** The cages that fold the solid, and those reaching past the run's ends. */
  folding: string[];
  clipped: string[];
  /** +1 when `out` is to the left of `along` (a counter-clockwise room), else −1. */
  hand: 1 | -1;
  /** The bench's height from ground: every height in `pts`, `faces` and
   *  `framePoint` is the frame's up plus it (the cages stay in the frame's). */
  lift: number;
}

/** The stations the starting mesh is laid at: every piece's ends, a straight
 *  face's every {@link SCULPT_STEP}, a corner's at least {@link CORNER_STATIONS}. */
function stationsOf(run: SculptRun): number[] {
  const out: number[] = [0];
  for (let k = 0; k + 1 < run.breaks.length; k++) {
    const a = run.breaks[k], b = run.breaks[k + 1], len = b - a;
    if (!(len > 1e-6)) continue;
    const corner = run.corners[k];
    const n = Math.max(corner ? CORNER_STATIONS : 1, Math.ceil(len / SCULPT_STEP));
    for (let i = 1; i <= n; i++) out.push(a + (len * i) / n);
  }
  return out;
}

/** The ring points seen from above, inside the outline: where a seat's or a
 *  step's edge runs (the profile's corners, one per depth). */
function seatCorners(ring: readonly ProfilePt[], width: number): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  const top = (x: number): number => Math.max(-Infinity, ...sliceAt(ring, x).map(([, hi]) => hi));
  ring.forEach((p, k) => {
    if (p.s || p.x < 1 || p.x > width - 1) return;
    const key = Math.round(p.x);
    if (seen.has(key)) return;
    if (p.y < Math.min(top(p.x - 0.5), top(p.x + 0.5)) - 1) return;
    seen.add(key);
    out.push(k);
  });
  return out;
}

const sub3 = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });

function newell(pts: readonly Vec3[]): Vec3 {
  let x = 0, y = 0, z = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    x += (a.y - b.y) * (a.z + b.z); y += (a.z - b.z) * (a.x + b.x); z += (a.x - b.x) * (a.y + b.y);
  }
  const l = Math.hypot(x, y, z);
  return l > 1e-12 ? { x: x / l, y: y / l, z: z / l } : { x: 0, y: 0, z: 0 };
}

function layoutOf(inst: ProductInstance, sb: SculptedBench): SculptLayout | null {
  const { width, height } = profileExtent(sb.profile);
  if (!(width > 1) || !(height > 1)) return null;
  const run = sculptRun(inst, sb, width);
  if (!run || !(run.length > 1)) return null;
  const extent = sculptExtent(run, sb.profile);
  const ring = sculptRing(sb.profile);
  const stations = stationsOf(run);
  const cages = activeCages(sb.lattices);
  const frame: F3[][] = stations.map((a) => ring.map((p) => deformStack(cages, [a, p.x, p.y])));
  const lift = benchLift(sb);
  const pts: Vec3[][] = frame.map((row) => row.map(([a, o, u]) => { const q = run.at(a, o); return { x: q.x, y: q.y, z: u + lift }; }));
  // Which side of the run the room is on.
  const mid = run.length / 2, t = run.dir(mid, width / 2), b0 = run.at(mid, 0), b1 = run.at(mid, width);
  const hand: 1 | -1 = t.x * (b1.y - b0.y) - t.y * (b1.x - b0.x) >= 0 ? 1 : -1;
  const faces: SweepFace[] = [];
  const nR = ring.length, nS = stations.length;
  // Each quad's place in `faces` (−1: no area), then its neighbours across its edges.
  const at: number[][] = [];
  for (let j = 0; j + 1 < nS; j++) {
    at.push([]);
    for (let k = 0; k < nR; k++) {
      const k2 = (k + 1) % nR;
      const quad = hand > 0 ? [pts[j][k2], pts[j + 1][k2], pts[j + 1][k], pts[j][k]] : [pts[j][k], pts[j + 1][k], pts[j + 1][k2], pts[j][k2]];
      const s = hand > 0 ? [!!ring[k2].s, !!ring[k2].s, !!ring[k].s, !!ring[k].s] : [!!ring[k].s, !!ring[k].s, !!ring[k2].s, !!ring[k2].s];
      const n = newell(quad);
      at[j].push(n.x || n.y || n.z ? faces.push({ pts: quad, normal: n, s }) - 1 : -1);
    }
  }
  const faceAt = (j: number, k: number): number | null => { const i = j >= 0 && j < at.length ? at[j][(k + nR) % nR] : -1; return i >= 0 ? i : null; };
  for (let j = 0; j < at.length; j++) {
    for (let k = 0; k < nR; k++) {
      const f = faces[at[j][k]];
      if (!f) continue;
      const k2 = (k + 1) % nR;
      // Along at k2 (to the next quad round the ring), across at j + 1, along at k, across at j.
      const lo = { nb: faceAt(j, k - 1), crease: !ring[k].s }, hi = { nb: faceAt(j, k + 1), crease: !ring[k2].s };
      const edges = hand > 0
        ? [hi, { nb: faceAt(j + 1, k), crease: false }, lo, { nb: faceAt(j - 1, k), crease: false }]
        : [lo, { nb: faceAt(j + 1, k), crease: false }, hi, { nb: faceAt(j - 1, k), crease: false }];
      f.nb = edges.map((e) => e.nb);
      f.crease = edges.map((e) => e.crease);
    }
  }
  for (const end of [0, nS - 1]) {
    const forward = (end === nS - 1) === hand > 0;
    const order = ring.map((_p, k) => (forward ? k : nR - 1 - k));
    const cap = order.map((k) => pts[end][k]);
    const n = newell(cap);
    if (n.x || n.y || n.z) faces.push({ pts: cap, normal: n, cap: true, s: order.map((k) => !!ring[k].s), ring: order });
  }
  // The plan outline: at each station, the point furthest from the faces and the nearest.
  const fronts: Vec2[] = [], backs: Vec2[] = [];
  for (let j = 0; j < nS; j++) {
    let lo = 0, hi = 0;
    for (let k = 1; k < nR; k++) {
      if (frame[j][k][1] < frame[j][lo][1]) lo = k;
      if (frame[j][k][1] > frame[j][hi][1]) hi = k;
    }
    backs.push({ x: pts[j][lo].x, y: pts[j][lo].y });
    fronts.push({ x: pts[j][hi].x, y: pts[j][hi].y });
  }
  const outline = [...backs, ...fronts.reverse()];
  const seatLines = seatCorners(ring, width).map((k) => pts.map((row) => ({ x: row[k].x, y: row[k].y })));
  const folding = cages.filter((c) => cageFolds(c)).map((c) => c.id);
  const clipped = (sb.lattices ?? []).filter((c) => c.box.along[0] < -1 || c.box.along[1] > run.length + 1).map((c) => c.id);
  const warnings: SculptWarning[] = [];
  if (run.chain.blocked) warnings.push('blocked');
  if (clipped.length) warnings.push('clipped');
  if (folding.length) warnings.push('fold');
  return { run, extent, ring, stations, pts, faces, outline, seatLines, warnings, folding, clipped, hand, lift };
}

/** A bench's height from ground (mm, never below the floor). */
export function benchLift(sb: Pick<SculptedBench, 'heightFromGround'>): number {
  const h = sb.heightFromGround;
  return h != null && Number.isFinite(h) && h > 0 ? h : 0;
}

// Its trestles stand under it: moving one leaves the solid as it is.
const LAYOUTS = contentMemo<SculptLayout | null>({ skip: new Set([...HEAVY_FIELDS, 'lights', 'trestles']) });

/** A sculpted bench resolved (memoised on its room and itself), or null when its
 *  run no longer resolves (its face gone) or its profile is empty. */
export function sculptLayout(inst: ProductInstance, sb: SculptedBench): SculptLayout | null {
  return LAYOUTS.get(`${inst.id}/${sb.id}`, () => [inst, sb], () => layoutOf(inst, sb));
}

/** The sculpted benches whose run no longer resolves (a face deleted): they go. */
export function orphanSculptedBenches(inst: ProductInstance): string[] {
  return sculptedBenchesOf(inst).filter((sb) => !sculptLayout(inst, sb)).map((sb) => sb.id);
}

/** The sculpted bench under a room-local point (its plan outline, `tol` around it). */
export function sculptBenchAt(inst: ProductInstance, p: Vec2, tol = 0): SculptedBench | null {
  const list = sculptedBenchesOf(inst);
  for (let i = list.length - 1; i >= 0; i--) {
    const lay = sculptLayout(inst, list[i]);
    if (!lay) continue;
    if (pointInPolygon(p, lay.outline)) return list[i];
    if (tol > 0 && nearRing(p, lay.outline, tol)) return list[i];
  }
  return null;
}

function nearRing(p: Vec2, ring: readonly Vec2[], tol: number): boolean {
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0;
    if (Math.hypot(p.x - a.x - dx * t, p.y - a.y - dy * t) <= tol) return true;
  }
  return false;
}

/** The solid's height band (mm above the floor): its lowest and highest points. */
export function sculptSpan(lay: SculptLayout): { z0: number; z1: number } {
  let z0 = Infinity, z1 = -Infinity;
  for (const row of lay.pts) for (const p of row) { z0 = Math.min(z0, p.z); z1 = Math.max(z1, p.z); }
  return { z0, z1 };
}

/**
 * The bench as triangles (room-local plan x, y; height z), for a 3D mesh:
 * smooth along the bench and across the profile's arcs and divisions, sharp at
 * its corners; flat caps at both ends. UVs: mm along the bench and round the
 * profile, so a wood's grain runs along it.
 */
export function sculptMeshBuffer(lay: SculptLayout): { pos: number[]; nrm: number[]; uv: number[] } {
  const { pts, ring, stations, hand } = lay;
  const nR = ring.length, nS = stations.length;
  const pos: number[] = [], nrm: number[] = [], uv: number[] = [];
  // Each quad's normal (station j..j+1, ring edge k..k+1), outward.
  const N: Vec3[][] = [];
  for (let j = 0; j + 1 < nS; j++) {
    const row: Vec3[] = [];
    for (let k = 0; k < nR; k++) {
      const k2 = (k + 1) % nR;
      const q = hand > 0 ? [pts[j][k2], pts[j + 1][k2], pts[j + 1][k], pts[j][k]] : [pts[j][k], pts[j + 1][k], pts[j + 1][k2], pts[j][k2]];
      row.push(newell(q));
    }
    N.push(row);
  }
  const perim: number[] = [0];
  for (let k = 1; k <= nR; k++) { const a = ring[k - 1], b = ring[k % nR]; perim.push(perim[k - 1] + Math.hypot(b.x - a.x, b.y - a.y)); }
  // The normal at station j, ring point k, as quad (·, e) sees it.
  const vn = (j: number, k: number, e: number): Vec3 => {
    const other = k === e ? (e - 1 + nR) % nR : (e + 1) % nR;
    const edges = ring[k].s ? [e, other] : [e];
    let x = 0, y = 0, z = 0;
    for (const jj of [j - 1, j]) {
      if (jj < 0 || jj >= N.length) continue;
      for (const ee of edges) { const n = N[jj][ee]; x += n.x; y += n.y; z += n.z; }
    }
    const l = Math.hypot(x, y, z) || 1;
    return { x: x / l, y: y / l, z: z / l };
  };
  const push = (p: Vec3, n: Vec3, u: number, v: number): void => { pos.push(p.x, p.y, p.z); nrm.push(n.x, n.y, n.z); uv.push(u, v); };
  for (let j = 0; j + 1 < nS; j++) {
    for (let e = 0; e < nR; e++) {
      const k2 = (e + 1) % nR;
      const A = { j, k: e }, B = { j: j + 1, k: e }, C = { j: j + 1, k: k2 }, D = { j, k: k2 };
      const order = hand > 0 ? [D, C, B, A] : [A, B, C, D];
      const vtx = order.map((q) => ({ p: pts[q.j][q.k], n: vn(q.j, q.k, e), u: stations[q.j], v: q.k === k2 && k2 === 0 ? perim[nR] : perim[q.k] }));
      for (const [a, b, c] of [[0, 1, 2], [0, 2, 3]]) for (const i of [a, b, c]) push(vtx[i].p, vtx[i].n, vtx[i].u, vtx[i].v);
    }
  }
  // The caps: the ring triangulated once, laid on each end.
  const flat = ring.map((p) => ({ x: p.x, y: p.y }));
  const index = new Map<Vec2, number>(flat.map((p, k) => [p, k]));
  const tris = earClip(flat);
  for (const f of lay.faces) {
    if (!f.cap || !f.ring) continue;
    const at = new Map<number, Vec3>();
    f.ring.forEach((k, i) => at.set(k, f.pts[i]));
    for (const tri of tris) {
      const [a, b, c] = tri.map((p) => at.get(index.get(p) ?? -1));
      if (!a || !b || !c) continue;
      const u = sub3(b, a), v = sub3(c, a);
      const cx = u.y * v.z - u.z * v.y, cy = u.z * v.x - u.x * v.z, cz = u.x * v.y - u.y * v.x;
      const tri3 = cx * f.normal.x + cy * f.normal.y + cz * f.normal.z < 0 ? [a, c, b] : [a, b, c];
      for (const p of tri3) push(p, f.normal, p.x, p.z);
    }
  }
  return { pos, nrm, uv };
}

// ---- Cages: which one is being edited (a view state, never stored) ----------

const ACTIVE = new Map<string, string>();
let activeRev = 0;
/** Bumped whenever a bench's active cage changes: what its handles were built from. */
export const activeCagesRev = (): number => activeRev;

/** The cage being edited on a bench: the one picked on its card, else its last. */
export function activeCageOf(sb: SculptedBench): Lattice | null {
  const list = sb.lattices ?? [];
  const id = ACTIVE.get(sb.id);
  return list.find((c) => c.id === id) ?? list[list.length - 1] ?? null;
}

/** Pick the cage a bench's handles edit. */
export function setActiveCage(benchId: string, cageId: string | null): void {
  if (ACTIVE.get(benchId) === (cageId ?? undefined)) return;
  if (cageId) ACTIVE.set(benchId, cageId); else ACTIVE.delete(benchId);
  activeRev++;
}

const PICK_MODE = new Map<string, CagePick>();
/** What a drag of one of a bench's cage points moves with it (a view state). */
export const cagePickOf = (benchId: string): CagePick => PICK_MODE.get(benchId) ?? 'point';
export function setCagePick(benchId: string, pick: CagePick): void {
  if (cagePickOf(benchId) === pick) return;
  if (pick === 'point') PICK_MODE.delete(benchId); else PICK_MODE.set(benchId, pick);
  activeRev++;
}

/** The cage point last pressed on a bench: where its move gizmo stands. */
export interface PickedPoint { cage: string; i: number; j: number; k: number }
const PICKED = new Map<string, PickedPoint>();
/** The picked point of the bench's active cage, while it is still one of its
 *  points that may move; else null. */
export function pickedCagePoint(sb: SculptedBench, extent: FrameExtent): PickedPoint | null {
  const p = PICKED.get(sb.id), c = activeCageOf(sb);
  if (!p || !c || c.id !== p.cage || p.i >= c.nx || p.j >= c.ny || p.k >= c.nz) return null;
  return pointFreedom(c, extent, p.i, p.j, p.k) ? p : null;
}
export function pickCagePoint(benchId: string, p: PickedPoint | null): void {
  const was = PICKED.get(benchId);
  if (was && p && was.cage === p.cage && was.i === p.i && was.j === p.j && was.k === p.k) return;
  if (p) PICKED.set(benchId, p); else PICKED.delete(benchId);
  activeRev++;
}

/** A cage point's place: room-local plan and height, laid on the run as the
 *  frame stands before any cage bends it (a cage is fixed in that frame). */
export function framePoint(lay: SculptLayout, p: F3): Vec3 {
  const q = lay.run.at(p[0], p[1]);
  return { x: q.x, y: q.y, z: p[2] + lay.lift };
}

/** The run's frame at a frame point (room local, unit): the plan direction along
 *  the bench and the one out from its faces into the room. */
export function frameAxes(lay: SculptLayout, p: F3): { along: Vec2; out: Vec2 } {
  const d = lay.run.dir(p[0], p[1]);
  return { along: d, out: lay.hand > 0 ? { x: -d.y, y: d.x } : { x: d.y, y: -d.x } };
}

/** A cage as the plan shows it: each column (i, j) at the middle of its points
 *  (room local), and the lines between neighbouring columns. */
export function cagePlan(lay: SculptLayout, c: Lattice): { columns: Array<{ i: number; j: number; at: Vec2 }>; lines: Vec2[][] } {
  const col = (i: number, j: number): Vec2 => {
    let x = 0, y = 0;
    for (let k = 0; k < c.nz; k++) { const p = framePoint(lay, cagePoint(c, i, j, k)); x += p.x; y += p.y; }
    return { x: x / c.nz, y: y / c.nz };
  };
  const grid: Vec2[][] = [];
  for (let i = 0; i < c.nx; i++) { grid.push([]); for (let j = 0; j < c.ny; j++) grid[i].push(col(i, j)); }
  const lines: Vec2[][] = [];
  for (let j = 0; j < c.ny; j++) lines.push(grid.map((row) => row[j]));
  for (let i = 0; i < c.nx; i++) lines.push(grid[i]);
  const columns: Array<{ i: number; j: number; at: Vec2 }> = [];
  for (let i = 0; i < c.nx; i++) for (let j = 0; j < c.ny; j++) columns.push({ i, j, at: grid[i][j] });
  return { columns, lines };
}

/** A cage as 3D draws it: its lines through its points (room-local plan and
 *  height), each point with whether it may move. */
export function cage3d(lay: SculptLayout, c: Lattice): { lines: Vec3[][]; points: Array<{ i: number; j: number; k: number; at: Vec3; free: boolean }> } {
  const lines = cageLines(c).map((l) => l.map((p) => framePoint(lay, p)));
  const points: Array<{ i: number; j: number; k: number; at: Vec3; free: boolean }> = [];
  for (let i = 0; i < c.nx; i++) for (let j = 0; j < c.ny; j++) for (let k = 0; k < c.nz; k++) {
    points.push({ i, j, k, at: framePoint(lay, cagePoint(c, i, j, k)), free: !!pointFreedom(c, lay.extent, i, j, k) });
  }
  return { lines, points };
}

// ---- A new bench --------------------------------------------------------------

/** The seat height a new sculpted bench's top is laid at (mm). */
export const SCULPT_SEAT_TOP = 450;

/** The height from ground that puts a profile's top at {@link SCULPT_SEAT_TOP}:
 *  a slab (Default, 100 thick) floats, a profile that tall or taller stands on
 *  the floor. */
export function seatLift(profile: readonly BenchProfilePoint[]): number {
  const { height } = profileExtent(profile);
  return Number.isFinite(height) ? Math.max(0, SCULPT_SEAT_TOP - height) : 0;
}

/** A new sculpted bench on a run, its profile copied from a preset, floating
 *  with its top at the seat height ({@link seatLift}). */
export function newSculptedBench(id: string, start: SculptEnd, end: SculptEnd, preset: { id: string; profile: BenchProfilePoint[] }): SculptedBench {
  const lift = seatLift(preset.profile);
  return { id, start, end, profile: preset.profile.map((p) => ({ ...p })), profilePresetId: preset.id, ...(lift > 0 ? { heightFromGround: lift } : {}) };
}

/** Another profile on a bench: its height from ground follows to keep the top
 *  at the seat height while it was there (left as typed otherwise). */
export function setBenchProfile(sb: SculptedBench, preset: { id: string; profile: BenchProfilePoint[] }): void {
  const auto = Math.abs(benchLift(sb) - seatLift(sb.profile)) < 0.5;
  sb.profile = preset.profile.map((q) => ({ ...q }));
  sb.profilePresetId = preset.id;
  if (!auto) return;
  const lift = seatLift(sb.profile);
  if (lift > 0) sb.heightFromGround = lift; else delete sb.heightFromGround;
}

/**
 * Where a bench from `sb.start`, walking toward `toward`, ends to be `length`
 * long on its middle line (the card's Length; a typed length in the tool): the
 * end slid along the faces the run walks to there and on to the last one's end,
 * found by halving. The longest it can be when `length` is more. Null when no
 * run from the start resolves.
 */
export function sculptEndAtLength(inst: ProductInstance, sb: Pick<SculptedBench, 'start' | 'profile' | 'cornerRadius'>, toward: SculptEnd, length: number): SculptEnd | null {
  const chain = runChain(inst, sb.start, toward);
  const width = profileExtent(sb.profile).width;
  if (!chain || !(width > 1)) return null;
  const last = chain.faces.length - 1;
  let segs: Array<{ f: RunFace; a: number; b: number }>;
  if (!last) {
    const f = chain.faces[0];
    const ps = Math.max(f.lo, Math.min(f.hi, paramOf(f, sb.start.s)));
    segs = [{ f, a: ps, b: paramOf(f, toward.s) >= ps ? f.hi : f.lo }];
  } else {
    segs = chain.faces.map((f, i) => ({ f, a: i === 0 ? chain.p0 : f.lo, b: i === last && chain.blocked ? chain.p1 : f.hi }));
  }
  const total = segs.reduce((s, g) => s + Math.abs(g.b - g.a), 0);
  const endAt = (t: number): SculptEnd => {
    let left = t;
    for (let i = 0; i < segs.length; i++) {
      const g = segs[i], span = Math.abs(g.b - g.a);
      if (left <= span || i === segs.length - 1) {
        const p = g.a + Math.sign(g.b - g.a) * Math.min(left, span);
        return { host: g.f.host, s: stationOf(g.f, p) };
      }
      left -= span;
    }
    return toward;
  };
  const lengthAt = (t: number): number => sculptRun(inst, { start: sb.start, end: endAt(t), cornerRadius: sb.cornerRadius }, width)?.length ?? 0;
  if (lengthAt(total) <= length) return endAt(total);
  let lo = 0, hi = total;
  for (let n = 0; n < 48 && hi - lo > 0.01; n++) {
    const m = (lo + hi) / 2;
    if (lengthAt(m) < length) lo = m; else hi = m;
  }
  return endAt(hi);
}

/** The room-local outline of a bench that is not stored yet (the tool's preview). */
export function previewOutline(inst: ProductInstance, sb: SculptedBench): Vec2[] | null {
  return layoutOf(inst, sb)?.outline ?? null;
}
